import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2, Plus, Edit, Users, Briefcase, Activity,
  Scale, Home, TrendingUp, Shield, Heart, ShoppingCart
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const DepartmentsManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDept, setNewDept] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: ''
  });

  // الأقسام القانونية التسعة لشركة وازن
  const defaultDepartments = [
    {
      name_ar: 'الدائرة العمالية',
      name_en: 'Labor Department',
      description_ar: 'متخصصة في قضايا العمل والعمال والتأمينات الاجتماعية',
      description_en: 'Specialized in labor, employment and social insurance cases',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      cases: 45
    },
    {
      name_ar: 'الدائرة التجارية',
      name_en: 'Commercial Department',
      description_ar: 'قضايا الشركات والعقود التجارية والإفلاس',
      description_en: 'Corporate, commercial contracts and bankruptcy cases',
      icon: ShoppingCart,
      color: 'from-green-500 to-green-600',
      cases: 38
    },
    {
      name_ar: 'دائرة الأحوال الشخصية',
      name_en: 'Personal Status Department',
      description_ar: 'قضايا الزواج والطلاق والنفقة والحضانة',
      description_en: 'Marriage, divorce, alimony and custody cases',
      icon: Heart,
      color: 'from-pink-500 to-pink-600',
      cases: 52
    },
    {
      name_ar: 'الدائرة العقارية',
      name_en: 'Real Estate Department',
      description_ar: 'قضايا العقارات والإيجارات والملكية',
      description_en: 'Real estate, rental and property cases',
      icon: Home,
      color: 'from-yellow-500 to-yellow-600',
      cases: 32
    },
    {
      name_ar: 'الدائرة الجزائية',
      name_en: 'Criminal Department',
      description_ar: 'القضايا الجنائية والجزائية',
      description_en: 'Criminal and penal cases',
      icon: Shield,
      color: 'from-red-500 to-red-600',
      cases: 25
    },
    {
      name_ar: 'دائرة التنفيذ',
      name_en: 'Execution Department',
      description_ar: 'تنفيذ الأحكام والسندات التنفيذية',
      description_en: 'Judgment execution and executive documents',
      icon: Briefcase,
      color: 'from-purple-500 to-purple-600',
      cases: 28
    },
    {
      name_ar: 'دائرة القضاء الإداري',
      name_en: 'Administrative Court Department',
      description_ar: 'القضايا الإدارية والتظلمات الحكومية',
      description_en: 'Administrative cases and government grievances',
      icon: Building2,
      color: 'from-indigo-500 to-indigo-600',
      cases: 18
    },
    {
      name_ar: 'دائرة الاستئناف',
      name_en: 'Appeals Department',
      description_ar: 'استئناف الأحكام والطعون',
      description_en: 'Appeals and challenges',
      icon: Scale,
      color: 'from-orange-500 to-orange-600',
      cases: 22
    },
    {
      name_ar: 'الدائرة المصرفية والمالية',
      name_en: 'Banking & Financial Department',
      description_ar: 'القضايا المصرفية والتمويلية',
      description_en: 'Banking and financing cases',
      icon: TrendingUp,
      color: 'from-cyan-500 to-cyan-600',
      cases: 35
    }
  ];

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data.length > 0 ? response.data : defaultDepartments);
    } catch (error) {
      setDepartments(defaultDepartments);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/departments', newDept);
      toast.success(i18n.language === 'ar' ? 'تم إضافة القسم بنجاح' : 'Department added successfully');
      setShowAddDept(false);
      setNewDept({ name_ar: '', name_en: '', description_ar: '', description_en: '' });
      fetchDepartments();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة القسم' : 'Failed to add department');
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي الأقسام' : 'Total Departments',
      value: departments.length,
      icon: Building2,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'إجمالي القضايا' : 'Total Cases',
      value: defaultDepartments.reduce((sum, d) => sum + d.cases, 0),
      icon: Briefcase,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'الأقسام النشطة' : 'Active Departments',
      value: departments.length,
      icon: Activity,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إدارة الأقسام القانونية' : 'Legal Departments Management'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'الدوائر القانونية التسعة لشركة وازن' : 'Wazin Law Firm\'s Nine Legal Departments'}
              </p>
            </div>
            <Dialog open={showAddDept} onOpenChange={setShowAddDept}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2">
                  <Plus className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إضافة قسم' : 'Add Department'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{i18n.language === 'ar' ? 'إضافة قسم قانوني جديد' : 'Add New Legal Department'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddDepartment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم بالعربية' : 'Name (Arabic)'}</Label>
                      <Input value={newDept.name_ar} onChange={(e) => setNewDept({...newDept, name_ar: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم بالإنجليزية' : 'Name (English)'}</Label>
                      <Input value={newDept.name_en} onChange={(e) => setNewDept({...newDept, name_en: e.target.value})} required />
                    </div>
                    <div className="col-span-2">
                      <Label>{i18n.language === 'ar' ? 'الوصف بالعربية' : 'Description (Arabic)'}</Label>
                      <Textarea value={newDept.description_ar} onChange={(e) => setNewDept({...newDept, description_ar: e.target.value})} required />
                    </div>
                    <div className="col-span-2">
                      <Label>{i18n.language === 'ar' ? 'الوصف بالإنجليزية' : 'Description (English)'}</Label>
                      <Textarea value={newDept.description_en} onChange={(e) => setNewDept({...newDept, description_en: e.target.value})} required />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowAddDept(false)}>
                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="btn-primary">
                      {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-2">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Departments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {defaultDepartments.map((dept, index) => (
              <Card
                key={index}
                className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${dept.color} opacity-5 group-hover:opacity-10 transition-all`} />
                <CardHeader className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${dept.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <dept.icon className="w-7 h-7 text-white" />
                    </div>
                    <Badge variant="outline" className="text-sm">
                      {dept.cases} {i18n.language === 'ar' ? 'قضية' : 'cases'}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl mb-2">
                    {i18n.language === 'ar' ? dept.name_ar : dept.name_en}
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {i18n.language === 'ar' ? dept.description_ar : dept.description_en}
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      <Edit className="w-4 h-4 ml-2" />
                      {i18n.language === 'ar' ? 'تعديل' : 'Edit'}
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1">
                      <Users className="w-4 h-4 ml-2" />
                      {i18n.language === 'ar' ? 'القضايا' : 'Cases'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DepartmentsManagement;